/**
 * somDeAviso — parte D1 (01/10/2026).
 *
 * Os dois sons do aviso na tela, sintetizados no Web Audio (sem arquivo): o "plim"
 * (aviso) e o "chamado" (crítico). São AGUDOS e curtos de propósito; o "chamar atenção"
 * do chat (`chamarAtencao.ts`) é grave e duplo — os três não se confundem. Parâmetros:
 * peça F de `docs/superpowers/specs/2026-10-01-notificacoes-aviso-e-som/desenho-front.md`.
 *
 * ⚠️ Autoplay: o navegador só libera áudio depois de um gesto na página. Por isso o
 *    contexto é UM por aba, criado/retomado no 1º gesto (`desbloquearAudio`, chamado
 *    pelo NotificationProvider) e reaproveitado. Ao RECEBER um aviso, contexto que não
 *    está `running` não toca: agendar num contexto suspenso faria o som sair atrasado,
 *    no próximo clique da pessoa. `manterAudioRetomado` cuida desse "próximo clique": retoma
 *    o contexto em todo gesto, não só no 1º.
 *
 * Nunca lança: o som é o enfeite do aviso; o aviso na tela chega de qualquer jeito.
 */

export type TipoDeSom = "aviso" | "critico";

type ConstrutorDeAudio = new () => AudioContext;

let contexto: AudioContext | null = null;

function construtor(): ConstrutorDeAudio | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: ConstrutorDeAudio }).webkitAudioContext
  );
}

/** O navegador tem Web Audio? Sem ele a tela nem oferece o interruptor. */
export function audioDisponivel(): boolean {
  return !!construtor();
}

/** O contexto de áudio da aba (um só, criado na 1ª vez). `null` sem Web Audio. O toque das ligações o reaproveita. */
export function contextoDaAba(): AudioContext | null {
  const Contexto = construtor();
  if (!Contexto) return null;
  if (!contexto || contexto.state === "closed") contexto = new Contexto();
  return contexto;
}

/**
 * Cria ou retoma o contexto da aba. Só surte efeito dentro de um gesto.
 * ⚠️ `!== "running"`, não `=== "suspended"`: o iOS também usa `interrupted` (ligação,
 * volta do segundo plano) e esse estado só volta com `resume()`.
 */
export function desbloquearAudio(): void {
  try {
    const ctx = contextoDaAba();
    if (ctx && ctx.state !== "running") void ctx.resume().catch(() => undefined);
  } catch {
    // Sem som; o aviso na tela segue.
  }
}

/**
 * Ouvinte PERMANENTE de gesto (captura, `pointerup` + `keydown` que não seja Escape): se o
 * contexto da aba existe e não está `running`, retoma. O `desbloquearAudio` do 1º gesto roda
 * uma vez só; sem isto, um contexto que ficou `suspended`/`interrupted` depois (iOS ao voltar
 * do segundo plano, uma ligação) nunca mais voltava, porque `tocarSomDeAviso` tenta o
 * `resume()` fora de gesto e o navegador o recusa. Não cria contexto: só cuida do que existe.
 * Devolve a função que remove os ouvintes. Nunca lança.
 */
export function manterAudioRetomado(): () => void {
  if (typeof window === "undefined") return () => undefined;
  function aoGesto(evento: Event) {
    if (evento instanceof KeyboardEvent && evento.key === "Escape") return;
    try {
      if (contexto && contexto.state !== "running") void contexto.resume().catch(() => undefined);
    } catch {
      // Sem som; o gesto da pessoa segue.
    }
  }
  window.addEventListener("pointerup", aoGesto, true);
  window.addEventListener("keydown", aoGesto, true);
  return () => {
    window.removeEventListener("pointerup", aoGesto, true);
    window.removeEventListener("keydown", aoGesto, true);
  };
}

interface Nota {
  frequencia: number;
  onda: OscillatorType;
  inicio: number;
  pico: number;
  ataque: number;
  fim: number;
}

function tocarNota(ctx: AudioContext, nota: Nota): void {
  const osc = ctx.createOscillator();
  const ganho = ctx.createGain();
  osc.type = nota.onda;
  osc.frequency.setValueAtTime(nota.frequencia, nota.inicio);
  // ⚠️ Sobe de 0,0001 em vez de começar no pico: o degrau seco estala no alto-falante.
  ganho.gain.setValueAtTime(0.0001, nota.inicio);
  ganho.gain.exponentialRampToValueAtTime(nota.pico, nota.inicio + nota.ataque);
  ganho.gain.exponentialRampToValueAtTime(0.0001, nota.fim);
  osc.connect(ganho).connect(ctx.destination);
  osc.start(nota.inicio);
  osc.stop(nota.fim + 0.02);
}

function tocarPlim(ctx: AudioContext): void {
  const t = ctx.currentTime;
  // C6 com o parcial G7 a −14 dB: o brilho de um sino pequeno, ~0,45 s.
  tocarNota(ctx, {
    frequencia: 1046.5,
    onda: "sine",
    inicio: t,
    pico: 0.12,
    ataque: 0.008,
    fim: t + 0.4,
  });
  tocarNota(ctx, {
    frequencia: 3136,
    onda: "sine",
    inicio: t,
    pico: 0.024,
    ataque: 0.008,
    fim: t + 0.25,
  });
}

/** A5 → C#6 → E6. */
const NOTAS_DO_CHAMADO = [880, 1108.7, 1318.5];

function tocarChamado(ctx: AudioContext): void {
  const t0 = ctx.currentTime;
  // A repetição (220 ms depois) é o que diz "não é um plim" sem subir o volume.
  for (const repeticao of [0, 1]) {
    NOTAS_DO_CHAMADO.forEach((frequencia, i) => {
      const inicio = t0 + repeticao * 0.64 + i * 0.14;
      tocarNota(ctx, {
        frequencia,
        onda: "triangle",
        inicio,
        pico: 0.18,
        ataque: 0.01,
        fim: inicio + 0.11,
      });
    });
  }
}

/**
 * Toca o som pedido. `noGesto: true` = chamado de dentro de um clique (o interruptor,
 * os "Ouvir"): agenda mesmo com o contexto ainda retomando. Devolve se agendou.
 */
export function tocarSomDeAviso(tipo: TipoDeSom, opcoes: { noGesto?: boolean } = {}): boolean {
  try {
    const ctx = contextoDaAba();
    if (!ctx) return false;
    if (ctx.state !== "running") {
      void ctx.resume().catch(() => undefined);
      if (!opcoes.noGesto) return false;
    }
    if (tipo === "critico") tocarChamado(ctx);
    else tocarPlim(ctx);
    return true;
  } catch {
    return false;
  }
}

/** Só para os testes: esquece o contexto da aba. */
export function _reiniciarAudioParaTeste(): void {
  contexto = null;
}
